import React, { useState, useEffect, useMemo } from "react";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import {
  normalizeDate,
  normalizePolicyReportProperties,
  revertNormalizeDate,
} from "../../utils/NormalizationUtil.ts";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import DatePicker from "../../components/Forms/DatePicker/DatePicker.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import { PolicyReferenceProps } from "../../enum/policy-fetch-type.enum.ts";
import { useLocation } from "react-router-dom";
import MultiSelect, { Option } from "../../components/Forms/MultiSelect.tsx";
import PoliciesReportDataGrid from "../../components/DataTables/PoliciesReportDataGrid.tsx";
import { PolicyReportMappingEnum } from "../../enum/normalization-mapping.enum.ts";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

const PAYMENT_PLACE_ZK_KEY = "2";

const INITIAL_FORM_STATE = {
  fromDate: "",
  toDate: "",
  company: "",
  policyType: "",
  agent: "",
  selectedOffices: [],
  identificationNumber: "",
  paymentPlace: "",
  shouldIncludeAnnulledPolicies: 0,
  reportNumber: "",
  reportDate: new Date().toISOString().split("T")[0],
};

const normalizeReportPolicies = (result: object) =>
  Object.values(result ?? []).map((policy) => {
    const normalizedPolicy = normalizePolicyReportProperties(policy);
    return {
      ...normalizedPolicy,
      installmentDate: normalizedPolicy.installmentDate
        ? normalizeDate(normalizedPolicy.installmentDate.trim())
        : "",
    };
  });

const InsuranceCompanyReport: React.FC<PolicyReferenceProps> = () => {
  const [formState, setFormState] = useState({ ...INITIAL_FORM_STATE });
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const { jwt } = useAuth();
  const [agentOptions, setAgentOptions] = useState<{ [key: string]: string }>(
    {}
  );
  const [insuranceCompanies, setInsuranceCompanies] = useState<{
    [key: string]: string;
  }>({});
  const [offices, setOffices] = useState<{ [key: string]: string }>({});
  const [officesOptions, setOfficesOptions] = useState<Option[]>([]);
  const [selectedOfficeValues, setSelectedOfficeValues] = useState<string[]>(
    []
  );
  const [selectedPolicies, setSelectedPolicies] = useState<[string, number][]>(
    []
  );
  const [policyTypes, setPolicyTypes] = useState<{ [key: string]: string }>({});
  const [paymentPlaces, setPaymentPlaces] = useState<{ [key: string]: string }>(
    {}
  );
  const [policies, setPolicies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isReportSaved, setIsReportSaved] = useState(false);

  const location = useLocation();

  const resetState = () => {
    setFormState({ ...INITIAL_FORM_STATE });
    setSelectedOfficeValues([]);
    setSelectedPolicies([]);
    setPolicies([]);
    setIsReportSaved(false);
    setAlert(null);
    setAlertType(undefined);
  };

  useEffect(() => {
    resetState();
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const [
          agents,
          companies,
          officesRes,
          policyTypesRes,
          paymentPlacesRes,
        ] = await Promise.all([
          InsuranceService.getAllAgents(jwt),
          InsuranceService.getAllInsuranceCompanies(jwt),
          InsuranceService.getAllOffices(jwt),
          InsuranceService.getAllPolicyTypes(jwt),
          PolicyService.fetchPayPlaces(jwt),
        ]);
        setAgentOptions(agents as { [key: string]: string });
        setInsuranceCompanies(companies as { [key: string]: string });
        setOffices(officesRes as { [key: string]: string });
        setPolicyTypes(policyTypesRes as { [key: string]: string });
        setPaymentPlaces(paymentPlacesRes as { [key: string]: string });
      } catch (err: any) {
        throw new Error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [jwt]);

  useEffect(() => {
    const fetchPolicyReportNumber = async (): Promise<void> => {
      setFormState({
        ...formState,
        reportNumber: (await InsuranceService.getPolicyReportNumber(
          jwt,
          formState.company
        )) as string,
      });
    };
    fetchPolicyReportNumber();
  }, [formState.company]);

  useEffect(() => {
    const updatedOfficeOptions = Object.entries(offices).map((office) => ({
      text: office[1],
      value: office[0],
      id: office[0],
      selected: selectedOfficeValues.includes(office[0]),
    }));
    setOfficesOptions(updatedOfficeOptions);
  }, [offices, selectedOfficeValues]);

  const handleOfficeSelectionChange = (selectedValues: string[]) => {
    setSelectedOfficeValues(selectedValues);
    setFormState((prevState) => ({
      ...prevState,
      selectedOffices: selectedValues,
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const { fromDate, toDate, company } = formState;
    if (!fromDate || !toDate) {
      setAlert("Моля, въведете стартова и крайна дата");
      setAlertType(FadeAlertType.error);
      return;
    }
    if (!company) {
      setAlert("Моля, изберете ЗК");
      setAlertType(FadeAlertType.error);
      return;
    }
    try {
      setIsLoading(true);
      const result = await PolicyService.fetchInsuranceCompanyReport(
        {
          ...formState,
          fromDate: revertNormalizeDate(formState.fromDate),
          toDate: revertNormalizeDate(toDate),
        },
        jwt
      );
      setPolicies(normalizeReportPolicies(result));
      setSelectedPolicies([]);
      setIsReportSaved(false);
    } catch (error) {
      setAlert("Грешка при зареждането на полици.");
      setAlertType(FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
    setAlert(null);
  };

  const selectedFilteredPolicies = useMemo(() => {
    return policies.filter((policy) =>
      selectedPolicies.some(
        ([polId, payNum]) =>
          polId === policy[PolicyReportMappingEnum.Pol_ID] &&
          payNum === policy[PolicyReportMappingEnum.Pay_Num]
      )
    );
  }, [policies, selectedPolicies]);

  const totalPremiumAmount = useMemo(() => {
    return selectedFilteredPolicies
      .reduce((sum, policy) => {
        const isPaidAtZK =
          policy[PolicyReportMappingEnum.Pay_Off] ===
          paymentPlaces[PAYMENT_PLACE_ZK_KEY];
        if (isPaidAtZK) return sum;
        return (
          sum + parseFloat(policy[PolicyReportMappingEnum.Total_Sum] || "0")
        );
      }, 0)
      .toFixed(2);
  }, [selectedFilteredPolicies, paymentPlaces]);

  const totalCommissionAmount = useMemo(() => {
    return selectedFilteredPolicies
      .reduce(
        (sum, policy) =>
          sum + parseFloat(policy[PolicyReportMappingEnum.Sum_Comm] || "0"),
        0
      )
      .toFixed(2);
  }, [selectedFilteredPolicies]);

  const reportSum = useMemo(() => {
    return (
      parseFloat(totalPremiumAmount) - parseFloat(totalCommissionAmount)
    ).toFixed(2);
  }, [totalPremiumAmount, totalCommissionAmount]);

  const savePolicyReportData = async () => {
    try {
      const { message, result } =
        await InsuranceService.saveInsuranceCompanyReport(
          jwt,
          formState.company,
          formState.reportNumber,
          formState.reportDate,
          selectedPolicies
        );

      setAlert(message || `Успешен запис на отчет №${formState.reportNumber}`);
      setAlertType(FadeAlertType.success);

      setPolicies(normalizeReportPolicies(result));
      setIsReportSaved(true);
    } catch (e) {
      setAlert("Грешка при запис на полица: " + e);
      setAlertType(FadeAlertType.error);
    }
  };

  return (
    <>
      <Breadcrumb pageName="Отчет към ЗК" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <div className="flex items-center justify-between">
          <SectionHeader title="Отчет към ЗК" />
          <div className="pr-4">
            <button
              type="button"
              onClick={resetState}
              title="Изчистване на всички данни"
              className="border-stroke hover:bg-accent hover:border-accent flex h-9 w-9 flex-shrink-0 items-center justify-center rounded border bg-white text-gray-500 hover:text-white"
            >
              <i className="fa-solid fa-trash text-sm" />
            </button>
            <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
              Изчистване на всички данни
            </span>
          </div>
        </div>

        <form onSubmit={handleSearch} className="p-3">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <div>
              <DatePicker
                label="От дата"
                name="fromDate"
                onChange={handleInputChange}
                value={formState.fromDate}
              />
            </div>
            <div>
              <DatePicker
                label="До дата"
                name="toDate"
                onChange={handleInputChange}
                value={formState.toDate}
              />
            </div>
            <div>
              <label className={labelClass}>ЗК</label>
              <SelectGroup
                value={formState.company}
                placeholder="Изберете компания"
                options={insuranceCompanies}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, company: value }))
                }
                name="company"
              />
            </div>
            <div>
              <label className={labelClass}>Вид Полица</label>
              <SelectGroup
                value={formState.policyType}
                placeholder="Изберете вид полица"
                options={policyTypes}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, policyType: value }))
                }
                name="policyType"
              />
            </div>
            <div>
              <label className={labelClass}>Агент</label>
              <SelectGroup
                value={formState.agent}
                placeholder="Изберете агент"
                options={agentOptions}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, agent: value }))
                }
                name="agent"
              />
            </div>
            <div>
              <label className={labelClass}>Вкл. анулирани полици</label>
              <SelectGroup
                value={formState.shouldIncludeAnnulledPolicies}
                placeholder="Вкл. анулирани полици"
                options={{ 0: "Не", 1: "Да" }}
                onChange={(value) =>
                  setFormState((prev) => ({
                    ...prev,
                    shouldIncludeAnnulledPolicies: value,
                  }))
                }
                name="shouldIncludeAnnulledPolicies"
              />
            </div>
            <div>
              <label className={labelClass}>Офис</label>
              <MultiSelect
                options={officesOptions}
                setOptions={setOfficesOptions}
                placeholder="Изберете офис"
                onSelectionChange={handleOfficeSelectionChange}
                id="multi-select-office"
              />
            </div>
            <div>
              <label className={labelClass}>Място на плащане</label>
              <SelectGroup
                value={formState.paymentPlace}
                placeholder="Изберете място"
                options={paymentPlaces}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, paymentPlace: value }))
                }
                name="paymentPlace"
              />
            </div>
          </div>

          <div className="mt-2 grid grid-cols-3 gap-2">
            <div className="col-start-2">
              <button
                type="submit"
                className="bg-primary w-full justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
              >
                Покажи
              </button>
            </div>
          </div>
        </form>

        <PoliciesReportDataGrid
          policies={policies}
          isLoading={isLoading}
          selectedPolicies={selectedPolicies}
          setSelectedPolicies={setSelectedPolicies}
          formState={formState}
          totals={{
            totalPremiumAmount,
            totalCommissionAmount,
            reportSum,
          }}
          labelMaps={{
            agents: agentOptions,
            companies: insuranceCompanies,
            offices: offices,
            policyTypes: policyTypes,
            paymentPlaces: paymentPlaces,
          }}
        />

        {/* Summary + save section */}
        <div className="mt-3 p-3">
          {/* Totals card */}
          <div className="divide-stroke border-stroke bg-gray-2 dark:divide-strokedark dark:border-strokedark dark:bg-meta-4 mb-3 grid grid-cols-3 divide-x rounded-md border">
            <div className="flex flex-col gap-1 px-4 py-3">
              <span className="text-xs font-medium text-black">
                Обща сума на премиите
              </span>
              <span className="text-sm font-bold text-black dark:text-white">
                €{totalPremiumAmount}
              </span>
            </div>
            <div className="flex flex-col gap-1 px-4 py-3">
              <span className="text-xs font-medium text-black">
                Общ комисион
              </span>
              <span className="text-sm font-bold text-black dark:text-white">
                €{totalCommissionAmount}
              </span>
            </div>
            <div className="flex flex-col gap-1 px-4 py-3">
              <span className="text-xs font-medium text-black">
                Сума за отчет
              </span>
              <span className="text-sm font-bold text-black dark:text-white">
                €{reportSum}
              </span>
            </div>
          </div>

          {/* Report number, date, save */}
          <div className="grid grid-cols-2 items-end gap-2 lg:grid-cols-4">
            <div>
              <label className={labelClass}>Номер на отчет</label>
              <input
                id="reportNumber"
                name="reportNumber"
                type="text"
                value={formState.reportNumber}
                disabled={isReportSaved}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    reportNumber: e.target.value,
                  }))
                }
                className={`${inputClass} ${
                  isReportSaved ? "cursor-not-allowed opacity-60" : ""
                }`}
              />
            </div>
            <div>
              <DatePicker
                name="reportDate"
                label="Дата на отчет"
                value={formState.reportDate}
                disabled={isReportSaved}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    reportDate: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={savePolicyReportData}
                disabled={isReportSaved}
                className={`bg-primary w-full justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90 ${
                  isReportSaved ? "cursor-not-allowed opacity-60" : ""
                }`}
              >
                Запис
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InsuranceCompanyReport;
